import { t } from 'i18next'
import memoize from 'lodash/memoize'
import sortBy from 'lodash/sortBy'

import { IConversationListItem, IGroupedConversations } from '@/types/gigiChat/Conversation'

const startOfDay = (date: Date): Date => {
    const start = new Date(date)
    start.setHours(0, 0, 0, 0)
    return start
}

const getRelativeDate = (date: Date): string => {
    const today: Date = startOfDay(new Date())
    const itemDate: Date = startOfDay(date)
    const oneDay: number = 24 * 60 * 60 * 1000
    const oneWeek: number = 7 * oneDay
    const oneMonth: number = 4 * oneWeek
    const oneYear: number = 12 * oneMonth

    const daysAgo: number = Math.floor((today.getTime() - itemDate.getTime()) / oneDay)
    const weeksAgo: number = Math.floor((today.getTime() - itemDate.getTime()) / oneWeek)
    const monthsAgo: number = Math.floor((today.getTime() - itemDate.getTime()) / oneMonth)
    const yearsAgo: number = Math.floor((today.getTime() - itemDate.getTime()) / oneYear)

    if (daysAgo < 1) return t('chat.conversationsList.today')
    if (daysAgo === 1) return t('chat.conversationsList.oneDayAgo')
    if (daysAgo < 7) return t('chat.conversationsList.daysAgo', { days: daysAgo })
    if (daysAgo === 7) return t('chat.conversationsList.oneWeekAgo')
    if (daysAgo < 14) return t('chat.conversationsList.oneWeekAgo')
    if (daysAgo === 14) return t('chat.conversationsList.weeksAgo', { weeks: weeksAgo })
    if (daysAgo < 21) return t('chat.conversationsList.weeksAgo', { weeks: weeksAgo })
    if (daysAgo === 21) return t('chat.conversationsList.weeksAgo', { weeks: weeksAgo })
    if (daysAgo < 30) return t('chat.conversationsList.weeksAgo', { weeks: weeksAgo })
    if (daysAgo >= 30 && daysAgo < 60) return t('chat.conversationsList.oneMonthAgo')
    if (daysAgo >= 60 && daysAgo < 90) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 90 && daysAgo < 120) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 120 && daysAgo < 150) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 150 && daysAgo < 180) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 180 && daysAgo < 210) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 210 && daysAgo < 240) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 240 && daysAgo < 270) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 270 && daysAgo < 300) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 300 && daysAgo < 330) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 330 && daysAgo < 360) return t('chat.conversationsList.monthsAgo', { months: monthsAgo })
    if (daysAgo >= 365 && daysAgo < 730) return t('chat.conversationsList.oneYearAgo')
    if (daysAgo >= 730 && daysAgo < 1095) return t('chat.conversationsList.yearsAgo', { years: yearsAgo })
    if (daysAgo >= 1095 && daysAgo < 1460) return t('chat.conversationsList.yearsAgo', { years: yearsAgo })
    if (daysAgo >= 1460 && daysAgo < 1825) return t('chat.conversationsList.yearsAgo', { years: yearsAgo })
    if (daysAgo >= 1825 && daysAgo < 2190) return t('chat.conversationsList.yearsAgo', { years: yearsAgo })
    if (daysAgo >= 2190) return t('chat.conversationsList.olderThan5Years')

    // Fallback for dates not covered by specific cases
    return `${daysAgo} days ago`
}

export const groupByRelativeDate = (data: IConversationListItem[], pinnedChats: string[]): IGroupedConversations => {
    return sortBy(data, (chat) => !pinnedChats.includes(chat.chatId)).reduce(
        (acc: { [key: string]: IConversationListItem[] }, item: IConversationListItem) => {
            let relativeDate: string = getRelativeDate(new Date(item.lastAskTimestamp || ''))
            if (pinnedChats.includes(item.chatId)) {
                relativeDate = 'Pinned'
            }
            if (!acc[relativeDate]) {
                acc[relativeDate] = []
            }
            acc[relativeDate].push(item)
            return acc
        },
        {},
    )
}

/**
 * @param key - name of url query param
 *
 * @return lowercase value at key
 */
function getAndRemovePropertyFromUrl(key: string): string | null {
    const url = window.location.href
    const urlParams = new URLSearchParams(new URL(url).search)
    const property = urlParams.get(key)
    urlParams.delete(key)

    // Create a new URL without the query parameters, missing hash, but we don't use it so it's ok
    const newUrl = window.location.origin + window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '')

    // Update the browser's address bar without reloading the page
    window.history.replaceState({}, '', newUrl)
    return property
}

/**
 * Why "once" ???
 * Because React is a piece of crap that renders things twice in dev mode, so I memoized it
 */
export const getAndRemovePropertyFromUrlOnce = memoize(getAndRemovePropertyFromUrl)
